import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import ReactDatePicker, { default as Calendar } from 'react-datepicker'
import styled from 'styled-components'
import { isSameDay, format } from 'date-fns'

import { BodyPortal } from 'components'
import Button from 'components/Buttons/Button'
import Box from 'components/Box'
import Footer from 'components/Footer'
import IconButton from 'components/Buttons/IconButton'
import Text from 'components/Text'
import PopOver from 'components/PopOver'
import { PopOverItem } from 'components/PopOver/types'

import {
  generateTimePickerOptions,
  generateTimePickerOptions12hrNumber,
  generateTimePickerOptions24,
  is12HrByCurrentLocale,
  parse12hr,
  parse12hrWithPad,
  parseDate,
} from 'utils/datetime'

import { PickerSize, getCustomCalendarDropdownStyle } from '../DatePicker'
import { DatetimePickerProps, MeridiemType } from './types'
import {
  constructDateTimeValue,
  convertToContinentalTime,
  formateTime,
  getDefaultMeridiem,
  isAfternoon,
  meridiemOpts,
} from './helper'
import { colors } from 'utils/colors'
import theme from 'modules/App/theme'

const DatetimePicker: FC<DatetimePickerProps> = ({
  canPickTime = false,
  dateFormat,
  disablePast = false,
  disableFuture = false,
  isClearable = false,
  name = 'datetimepicker',
  onChange,
  onRemove,
  onToggleCalendarOpen,
  pickerSize = 'small',
  showTimeSelect: showTimeSelectProp = canPickTime,
  textInputInline = false,
  timeInputLabel = 'time_lbl',
  initialValue,
  isPortaled,
  showMonthYearDropdown = false,
  onCalendarClose,
  showTimeWithMeridiem = false,
  isFooterVisible = true,
  ...props
}) => {
  const calendarRef = useRef<ReactDatePicker>(null)
  const timePickerInputSearchRef = useRef<HTMLInputElement>(null)
  const timeInputErrorRef = useRef<{ value: boolean }>({ value: false })
  const is12HourFormat = !showTimeWithMeridiem && is12HrByCurrentLocale()
  const [showTimeSelect] = useState(showTimeSelectProp)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [date, setDate] = useState<Date | null>(parseDate(initialValue) || new Date())
  const [meridiem, setMeridiem] = useState<MeridiemType>(
    initialValue || props.minDate ? getDefaultMeridiem(parseDate(initialValue) || props.minDate) : 'AM',
  )

  const timeFormat = showTimeWithMeridiem ? 'h:mm a' : is12HourFormat ? 'h:mm' : 'H:mm'
  const [time, setTime] = useState<string>(format(parseDate(initialValue) || new Date(), timeFormat))

  const handleChangeDate = (value: Date | null) => {
    setDate(value)
    if (showTimeWithMeridiem) {
      setTime(timePickerInputSearchRef?.current?.value || '')
    }
  }

  const handleChange = () => {
    if (!date || !calendarRef.current) {
      return
    }

    if (timeInputErrorRef.current.value) {
      return
    }

    calendarRef.current.setOpen(false)

    let timeValue
    if (showTimeWithMeridiem) {
      const { hours, minutes, meridiem } = formateTime(timePickerInputSearchRef?.current?.value || time)
      const { modifiedHours, modifiedMeridiem } = convertToContinentalTime({
        hours: Number(hours),
        minutes: Number(minutes),
        meridiem,
        inputValue: '',
      })
      timeValue = `${modifiedHours}:${minutes} ${modifiedMeridiem}`
    } else {
      timeValue = is12HourFormat ? `${time} ${meridiem}` : time
    }
    const constructed = constructDateTimeValue({ date, time: timeValue, showTimeWithMeridiem })
    constructed && onChange(constructed)
  }

  const handleCancel = () => {
    if (!date || !calendarRef.current) {
      return
    }
    calendarRef.current.setOpen(false)
    setIsCalendarOpen(false)
    onCalendarClose?.()
  }

  const Container = ({ children }: { children: React.ReactNode }) => {
    const resolveLeftComponent = () => {
      const cancelButtonComponent = (
        <Button disabled={!Boolean(date)} onClick={handleCancel} variant="outline">
          cancel_btn
        </Button>
      )

      // cleanup later: unused code
      if (isClearable) {
        return (
          <Box display="flex">
            {canPickTime && cancelButtonComponent}
            <IconButton
              icon={['far', 'trash-alt']}
              iconColor="red"
              ml="8px"
              onClick={onRemove}
              variant="criticalGhost"
            />
          </Box>
        )
      }

      return canPickTime ? cancelButtonComponent : null
    }

    return (
      <ContainerWrapper
        inline={props.inline}
        pickerSize={pickerSize}
        showTimeSelect={showTimeSelect}
        textInputInline={textInputInline}
        showMonthYearDropdown={showMonthYearDropdown}
      >
        {children}
        <Footer
          padding="16px 16px 0px"
          leftButtonComponent={resolveLeftComponent()}
          rightButtonComponent={
            <Button disabled={!Boolean(date)} onClick={handleChange} variant="outline">
              apply_btn
            </Button>
          }
          showFooterLine
          isVisible={isFooterVisible}
        />
      </ContainerWrapper>
    )
  }

  useEffect(() => {
    if (onToggleCalendarOpen) {
      onToggleCalendarOpen(isCalendarOpen)
    }
  }, [isCalendarOpen, onToggleCalendarOpen])

  useEffect(() => {
    const parsed = parseDate(initialValue)
    if (!parsed) {
      return
    }

    setDate(parsed)
    setTime(format(parsed, timeFormat))
    setMeridiem(getDefaultMeridiem(parsed))
  }, [initialValue, timeFormat])

  const { minDate } = props

  const timePickerOptions = useMemo(() => {
    let hourOptions
    if (showTimeWithMeridiem) {
      hourOptions = generateTimePickerOptions(false, !showTimeWithMeridiem)
    } else {
      hourOptions = is12HourFormat ? generateTimePickerOptions12hrNumber() : generateTimePickerOptions24()
    }

    if (!minDate || !date || !meridiem) {
      return hourOptions
    }

    if (isSameDay(minDate, date)) {
      const nowFormatted = format(new Date(), 'hh:mm')
      const nowMeridiem = getDefaultMeridiem(parseDate(new Date()))
      const nowOptionDate = parse12hrWithPad(nowFormatted, nowMeridiem)
      return hourOptions.filter((i) => {
        if (is12HourFormat) {
          return parse12hr(i.value, meridiem) >= parse12hr(format(minDate, 'h:mm'), meridiem)
        } else if (showTimeWithMeridiem) {
          const [hStr, mStr] = i.label.split(':')
          const [m, withMeridiem] = mStr.split(' ')
          const hourOptionDate = parse12hrWithPad(`${hStr}:${m}`, withMeridiem as MeridiemType)
          return hourOptionDate >= nowOptionDate
        }
        return true
      })
    }
    return hourOptions
  }, [is12HourFormat, date, meridiem, minDate, showTimeWithMeridiem])

  const meridiemOptions = useMemo(() => {
    if (props.minDate && date && date <= props.minDate && isAfternoon(props.minDate)) {
      return meridiemOpts.filter((i) => i.value === 'PM')
    }

    return meridiemOpts
  }, [props.minDate, date])

  let selectedTime
  if (showTimeWithMeridiem) {
    const timeWithMeridiem = `${time}`
    selectedTime = timePickerOptions.find((i) => i.label === timeWithMeridiem) ?? {
      label: timeWithMeridiem,
      value: timeWithMeridiem,
    }
  } else {
    selectedTime = timePickerOptions.find((i) => i.value === time) ?? { label: time, value: time }
  }

  return (
    <CalendarWrapper disabled={props.disabled}>
      <Calendar
        {...props}
        calendarContainer={Container}
        popperContainer={isPortaled ? BodyPortal : undefined}
        customInput={props.customInput}
        dateFormat={dateFormat ? dateFormat : showTimeSelect ? 'PPPp' : 'PPP'}
        minDate={props.minDate ?? (disablePast ? new Date() : null)}
        maxDate={props.maxDate ?? (disableFuture ? new Date() : null)}
        name={name}
        onCalendarClose={() => setIsCalendarOpen(false)}
        onCalendarOpen={() => setIsCalendarOpen(true)}
        onChange={handleChangeDate}
        popperModifiers={{
          offset: {
            offset: 20,
          },
        }}
        ref={calendarRef}
        selected={date}
        shouldCloseOnSelect={false}
        showPopperArrow={false}
        strictParsing
        useWeekdaysShort
        showMonthDropdown={showMonthYearDropdown}
        showYearDropdown={showMonthYearDropdown}
      >
        {(canPickTime || showTimeSelect) && (
          <Box
            padding="10px"
            alignItems="center"
            display="flex"
            justifyContent="center"
            gap="1rem"
            borderTop={`1px solid ${colors.defaultBorder}`}
          >
            <Text color={props.disabled ? 'gray4' : props.readOnly ? 'gray5' : 'grayShadeBlack2'}>
              {timeInputLabel}
            </Text>
            <TimePickerWrapper showTimeWithMeridiem={showTimeWithMeridiem}>
              <PopOver
                onChange={(value?: PopOverItem) => {
                  timeInputErrorRef.current.value = false
                  if (Array.isArray(value)) {
                    return setTime(showTimeWithMeridiem ? value[0].label : value[0].value)
                  }
                  setTime((showTimeWithMeridiem ? value?.label : value?.value) || '')
                }}
                onToggleMenu={(isOpen) => {
                  if (!isOpen) {
                    if (timePickerInputSearchRef?.current?.value) {
                      const { numHours, numMinutes, meridiem } = formateTime(timePickerInputSearchRef?.current?.value)
                      if (
                        numHours > 24 ||
                        numMinutes > 59 ||
                        (meridiem && !['AM', 'PM'].includes((meridiem || '').trim().toUpperCase()))
                      ) {
                        return
                      }
                      setTime(timePickerInputSearchRef.current.value)
                    }
                  }
                }}
                options={timePickerOptions}
                placement="top"
                value={selectedTime}
                width={100}
                isInputSearchHandle={showTimeWithMeridiem}
                timeInputRef={timePickerInputSearchRef}
                timeInputErrorRef={timeInputErrorRef}
                isDisabled={!canPickTime}
              />
            </TimePickerWrapper>

            {is12HourFormat && !showTimeWithMeridiem && (
              <TimePickerWrapper showTimeWithMeridiem={showTimeWithMeridiem}>
                <PopOver
                  onChange={(value?: PopOverItem) => {
                    if (Array.isArray(value)) {
                      return setMeridiem(value[0].value as MeridiemType)
                    }
                    setMeridiem(value?.value as MeridiemType)
                  }}
                  options={meridiemOptions}
                  placement="top"
                  value={meridiemOptions.find((i) => i.value === meridiem)}
                  width={80}
                  isDisabled={!canPickTime}
                />
              </TimePickerWrapper>
            )}
          </Box>
        )}
      </Calendar>
    </CalendarWrapper>
  )
}

const CalendarWrapper = styled.div<{ disabled?: boolean }>`
  .react-datepicker__input-container {
    width: inherit;
  }

  .react-datepicker-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  }
`

const ContainerWrapper = styled.div<{
  inline?: boolean
  pickerSize?: PickerSize
  showTimeSelect?: boolean
  textInputInline?: boolean
  showMonthYearDropdown: boolean
}>`
  width: 300px;
  border-radius: 4px;
  background-color: ${(p) => p.theme.colors.white};
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  padding-top: 16px;
  padding-bottom: 16px;

  .react-datepicker {
    border: ${(p) => (p.pickerSize === 'small' ? 20 : 0)}px;
    box-shadow: ${(p) =>
      p.inline
        ? 'none'
        : '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 0 rgba(0, 0, 0, 0.05)'};
    height: ${(p) => (p.showTimeSelect ? '100%' : `${p.pickerSize === 'small' ? 285 : 350}px`)};
    width: ${(p) => (p.showTimeSelect ? '100%' : `${p.pickerSize === 'small' ? 250 : 375}px`)};
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    width: 2rem;
  }

  .react-datepicker__day {
    ${(p) =>
      p.theme.font({
        color: 'gray7',
        size: p.pickerSize === 'small' ? 14 : 16,
        weight: 'normal',
      })};
    margin: 2px 5px;
    padding: ${(p) => (p.pickerSize === 'small' ? 1 : 5)}px;
  }

  .react-datepicker__current-month {
    user-select: none;
    ${({ showMonthYearDropdown }) =>
      showMonthYearDropdown
        ? `display: none;`
        : `
            height: 28px;
            display: flex;
            justify-content: center;
            align-items: center;
        `};
  }

  .react-datepicker__day-name {
    ${(p) =>
      p.theme.font({
        color: p.pickerSize === 'small' ? 'gray5' : 'gray7',
        size: p.pickerSize === 'small' ? 10 : 12,
        weight: p.pickerSize === 'small' ? 'bold' : 'normal',
      })};
    margin: ${(p) => (p.pickerSize === 'small' ? '0 5px 0' : '10px 5px 0')};
    padding: ${(p) => (p.pickerSize === 'small' ? '10px 2px 0 0' : '10px 5px 0 5px')};
    text-transform: uppercase;
  }

  .react-datepicker__day--outside-month {
    visibility: hidden;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: ${(p) => p.theme.colors.blue1};
    color: ${(p) => p.theme.colors.white} !important;
  }

  .react-datepicker__day--disabled {
    ${(p) =>
      p.theme.font({
        color: 'gray4',
        size: p.pickerSize === 'small' ? 14 : 16,
        weight: 'normal',
      })};
  }

  .react-datepicker__header {
    background-color: transparent;
    border-bottom: none !important;
    padding-top: 0;
  }

  .react-datepicker__month {
    margin: 0;
  }

  .react-datepicker__month-container {
    float: none;
    height: 100%;
  }

  .react-datepicker__navigation--next {
    background-image: url('/assets/angle-right.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 7px;
    border: 1px solid;
    border-color: ${(p) => p.theme.colors.gray4};
    border-radius: 4px;
    margin-top: 6px;
    margin-right: ${(p) => (p.pickerSize === 'small' ? 0 : 15)}px;
    padding: 13px 13px;
  }

  .react-datepicker__navigation--previous {
    background-image: url('/assets/angle-left.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 7px;
    border: 1px solid;
    border-color: ${(p) => p.theme.colors.gray4};
    border-radius: 4px;
    margin-top: 6px;
    margin-left: ${(p) => (p.pickerSize === 'small' ? 0 : 15)}px;
    padding: 13px 13px;
  }

  & .react-datepicker__navigation--next:hover,
  & .react-datepicker__navigation--previous:hover {
    background-color: ${(p) => p.theme.colors.gray1};
    border-color: ${(p) => p.theme.colors.blue1};
  }

  & .react-datepicker__header__dropdown {
    padding-top: 0;
  }

  ${(p) => getCustomCalendarDropdownStyle({ pickerSize: p.pickerSize })}
`

const TimePickerWrapper = styled.div<{ showTimeWithMeridiem: boolean }>`
  border: 1px solid
    ${({ theme, showTimeWithMeridiem }) =>
      !showTimeWithMeridiem ? theme.colors.defaultBorder : theme.colors.transparent};
  border-radius: 4px;
  flex: 0 0 40%;
  color: ${theme.colors.gray7};
`

export default DatetimePicker
